import { useState } from 'react';
import { FieldExtensionComponentProps } from "@backstage/plugin-scaffolder-react";
import {
	fetchApiRef,
	useApi,
  } from '@backstage/core-plugin-api';
  import {
	Progress,
	ErrorPanel,
  } from '@backstage/core-components';
import { useAsync } from 'react-use';
import { FormControl, FormHelperText, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { sortBy } from 'lodash';
import { z } from 'zod';
import { AzureDevOpsOauthWrapper } from '../AzureDevOpsOauthWrapper';
import { useAzureDevOpsOauthContext } from '../../hooks/useOauthContext';
import { AzureDevOpsProjectFieldComponent, AzureDevOpsProjectFieldScopes } from '../AzureDevOpsProjectField';
import { AzureDevOpsRepoFieldComponent } from '../AzureDevOpsRepoField';
import { azureDevOpsFetch } from '../helpers';

const uiOptionsSchema = z.object({
	organization: z.string().describe('The Azure DevOps organization to query'),
	project: z.string().optional().describe('The Azure DevOps project to query'),
	repo: z.string().optional().describe('The Azure DevOps repository to query'),
	title: z.string().optional().describe('The title of the field'),
	tokenSecretName: z.string().optional().describe('The name of the secret to store the token in'),
	extraScopes: z.array(z.string()).optional().describe('Additional OAuth scopes to request'),
});

export const AzureDevOpsBranchFieldSchema = makeFieldSchema({
	output: x => x.string(),
	uiOptions: zImpl => zImpl.object({
		organization: zImpl.string().describe('The Azure DevOps organization to query'),
		project: zImpl.string().optional().describe('The Azure DevOps project to query'),
		repo: zImpl.string().optional().describe('The Azure DevOps repository to query'),
		title: zImpl.string().optional().describe('The title of the field'),
		tokenSecretName: zImpl.string().optional().describe('The name of the secret to store the token in'),
		extraScopes: zImpl.array(zImpl.string()).optional().describe('Additional OAuth scopes to request'),
	}),
});

export const AzureDevOpsBranchFieldScopes = ['https://app.vssps.visualstudio.com/vso.code'];

const AzureDevOpsBranchFieldComponent = (
	props: FieldExtensionComponentProps<string, { organization: string, project?: string, repo?: string }> & {
		project?: string,
		repo?: string,
	},
) => {
	const fetchApi = useApi(fetchApiRef);
	const { token } = useAzureDevOpsOauthContext();
	const [branches, setBranches] = useState<{ label: string, value: string }[]>([]);
	const project = props.uiSchema['ui:options']?.project ?? props.project
	const repo = props.uiSchema['ui:options']?.repo ?? props.repo

	const { error } = useAsync(async () => {
		if (!project || !repo) {
			return;
		}
		
		const organization = props.uiSchema['ui:options']?.organization;
		const body = await azureDevOpsFetch({
			url: `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repo}/refs?filter=heads/&api-version=7.2-preview.2`,
			token,
			fetchApi,
		});	
		const fetchedBranches = body.value.map((branch: any) => {
			const name = branch.name.split('/').slice(2).join('/');
			return {
				label: name,
				value: name,
			}
		});
		setBranches(sortBy(fetchedBranches, 'label'));
	}, [project, repo, token]);

	if (!project || !repo) {
		return <></>;
	}

	if (error) {
		return <ErrorPanel error={error} />;
	}
	if (!branches) {
		return <Progress />;
	}

	return (
		<FormControl
			margin="normal"
			required={props.required}
			error={(props?.rawErrors?.length || 0) > 0 && !props.formData}
		>
			<Autocomplete
				options={branches}
				freeSolo={false}
				renderInput={(params) => <TextField {...params} label="Azure DevOps Branch" variant="outlined" />}
				getOptionLabel={(option) => option.label}
				onChange={(_, value) => {
					props.onChange(`${project}/${repo}/${value?.value}`);
				}}
			/>
			<FormHelperText>Select a branch from the list.</FormHelperText>
		</FormControl>
	)
};

export const AzureDevOpsBranchField = (props: FieldExtensionComponentProps<string, {
	organization: string,
	project?: string,
	repo?: string,
	tokenSecretName? : string,
	extraScopes? : string[],
}>) => {
	const [project, setProject] = useState(props.uiSchema?.['ui:options']?.project ?? '');
	const [repo, setRepo] = useState(props.uiSchema?.['ui:options']?.repo ?? '');

	if (!props.uiSchema) {
		return <ErrorPanel error={new Error("No uiSchema provided")} />;
	}
	
	const result = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);
	const addProjectField = !props.uiSchema['ui:options']?.project;
	const addRepoField = !props.uiSchema['ui:options']?.repo;
	const extraScopes = props.uiSchema['ui:options']?.extraScopes ?? [];
	const tokenSecretName = props.uiSchema['ui:options']?.tokenSecretName;
	
	if (result.success) {
		const requiredScopes = Array.from(new Set(AzureDevOpsBranchFieldScopes.concat(AzureDevOpsProjectFieldScopes).concat(extraScopes)));
		if (addProjectField || addRepoField) {
			return (
				<AzureDevOpsOauthWrapper {...props} scopes={requiredScopes} tokenSecretName={tokenSecretName} >
					{ addProjectField && <AzureDevOpsProjectFieldComponent {...props} onProjectChange={(value: string) => setProject(value)} /> }
					{ (addRepoField && project) && <AzureDevOpsRepoFieldComponent {...props} project={project} onRepoChange={(value: string) => setRepo(value)} /> }
					{ project && repo && <AzureDevOpsBranchFieldComponent {...props} project={project} repo={repo} /> }
				</AzureDevOpsOauthWrapper>
			)
		}
		
		return (
			<AzureDevOpsOauthWrapper {...props} scopes={AzureDevOpsBranchFieldScopes}>
				<AzureDevOpsBranchFieldComponent {...props} project={project} repo={repo} />
			</AzureDevOpsOauthWrapper>
		)
	}

	return <ErrorPanel error={result.error} />;
};
